import { createSelector } from 'reselect';

import { MULTIMODAL_TYPE } from '../../../constants';
import {
    selectSearchResults,
    selectTripMetadata,
} from './trips';

function isNearbyTrip(tripMetadata, tripId) {
    const {
        exactOriginMatch,
        exactDestMatch,
    } = tripMetadata[tripId];

    return !exactOriginMatch || !exactDestMatch;
}

function minPriceByCarrier(allowNearbyIsFiltered, trips = [], tripMetadata) {
    let minPriceObj = {};
    trips.forEach((trip) => {
        const tripLegs = trip.isRouted
            ? trip.tripLegs.filtered
            : [trip];

        const {
            tripModes,
            tripId,
        } = trip;
        const isMultiModal = new Set([...tripModes]).size > 1;

        tripLegs.forEach((leg) => {
            const { carrierCode } = leg;
            const tripModes = isMultiModal
                ? [MULTIMODAL_TYPE]
                : leg.tripModes;

            if (!minPriceObj[carrierCode]) {
                minPriceObj[carrierCode] = {};
            }

            if (allowNearbyIsFiltered && isNearbyTrip(tripMetadata, tripId)) {
                return;
            }

            tripModes.forEach((travelMode) => {
                if (!minPriceObj[carrierCode][travelMode]) {
                    minPriceObj[carrierCode][travelMode] = {};
                }

                const minPricing = minPriceObj[carrierCode][travelMode];
                const USDPricing = trip._priceInUSDForSorting;
                if (!minPricing.USDForSort || minPricing.USDForSort > USDPricing) {
                    minPriceObj[carrierCode][travelMode] = {
                        pricing: trip.pricing,
                        USDForSort: USDPricing,
                    };
                }
            });
        });
    });

    return minPriceObj;
}

const selectMinPriceByCarrier = createSelector(
    (state, searchParams, allowNearbyIsFiltered) => allowNearbyIsFiltered,
    selectSearchResults,
    selectTripMetadata,
    minPriceByCarrier,
);

function groupCarriersByMode(allowNearbyIsFiltered, trips = [], tripMetadata) {
    const carrierByModeMap = {};
    trips.forEach((trip) => {
        let {
            tripId,
            isRouted,
            tripLegs,
            tripModes,
        } = trip;

        tripLegs = isRouted
            ? tripLegs.filtered
            : [trip];

        const isMultiModal = new Set([...tripModes]).size > 1;

        tripLegs.forEach((leg) => {
            let {
                carrierCode,
                tripModes,
            } = leg;

            if (allowNearbyIsFiltered && isNearbyTrip(tripMetadata, tripId)) {
                return;
            }

            if (isMultiModal) {
                tripModes = [MULTIMODAL_TYPE];
            }

            tripModes.forEach((mode) => {
                if (!carrierByModeMap[mode]) {
                    carrierByModeMap[mode] = new Set();
                }

                carrierByModeMap[mode].add(carrierCode);
            });
        });
    });

    return Object.entries(carrierByModeMap)
        .reduce((map, [travelMode, set]) => ({
            ...map,
            [travelMode]: [...set],
        }), {});
}

const selectCarriersByMode = createSelector(
    (state, searchParams, allowNearbyIsFiltered) => allowNearbyIsFiltered,
    selectSearchResults,
    selectTripMetadata,
    groupCarriersByMode,
);

function groupModesByCarriers(carriersByModeMap) {
    return Object.entries(carriersByModeMap).reduce((map, [travelMode, carriers]) => {
        return carriers.reduce((map, carrierCode) => {
            if (map[carrierCode] && map[carrierCode].includes(travelMode)) {
                return map;
            }

            return {
                ...map,
                [carrierCode]: [
                    ...map[carrierCode] || [],
                    travelMode,
                ],
            };
        }, map);
    }, {});
}

const selectCarrierModesMap = createSelector(
    selectCarriersByMode,
    groupModesByCarriers,
);

const selectCarriersTripData = createSelector(
    selectMinPriceByCarrier,
    (minByCarrier) => {
        return Object.entries(minByCarrier).reduce((map, [carrierCode, travelModeObj]) => {
            return Object.entries(travelModeObj).reduce((map, [travelMode, pricingObj]) => {
                return {
                    ...map,
                    [carrierCode]: {
                        ...map[carrierCode] || {},
                        [travelMode]: {
                            minPricing: pricingObj.pricing,
                        },
                    },
                };
            }, map);
        }, {});
    },
);

export {
    selectCarrierModesMap,
    selectCarriersByMode,
    selectCarriersTripData,
    selectMinPriceByCarrier,
};
